import type { ComponentPropsWithoutRef, ReactNode } from 'react';

import { cn } from '@polyseme/utils/cn/cn-utils';
import { ObjectUtils } from '@polyseme/utils/typescript/typescript-utils';
import { PopoverClose } from '@radix-ui/react-popover';

import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Icon } from '../icons';

const buttonVariant = cn('shrink-0 rounded-full');

type LearnMoreContentProps = {
  items: {
    description: ReactNode;
    title?: string;
  }[];
};

export type LearnMoreProps = ComponentPropsWithoutRef<'button'> & {
  buttonLabel?: string;
} & (
    | {
        items: LearnMoreContentProps['items'];
        title: string;
        variant?: 'modal' | 'popover';
      }
    | {
        link: string;
        variant: 'link';
      }
  );

const LearnMoreContent = ({ items }: LearnMoreContentProps) => (
  <ul className="space-y-4">
    {items.map(({ description, title }, index) => (
      <li
        key={`${title}-${index}`}
        className="rounded-lg border border-gray-100 p-2 text-sm text-gray-600"
      >
        {!!title && <h5 className="font-bold">{title}</h5>}
        <p className="whitespace-pre-line">{description}</p>
      </li>
    ))}
  </ul>
);

const closeLabel = 'Fermer';

export const LearnMore = ({
  buttonLabel = 'En apprendre plus',
  className,
  ...props
}: LearnMoreProps) => {
  if (props.variant === 'link') {
    return (
      <Button
        isIcon
        aria-label={buttonLabel}
        className={cn(buttonVariant, className, 'p-0 lg:p-0')}
        {...ObjectUtils.without(props, 'variant')}
        size="sm"
        variant="ghost"
        onClick={() => window.open(props.link, '_blank')}
      >
        <Icon
          className="text-primary"
          name="phosphor-info"
          size="lg"
          weight="fill"
        />
        <span className="sr-only">{buttonLabel}</span>
      </Button>
    );
  }

  const { items, title, variant, ...rest } = props;

  if (variant === 'modal') {
    return (
      <Dialog>
        <DialogTrigger asChild>
          <Button
            isIcon
            aria-label={buttonLabel}
            className={cn(buttonVariant, className)}
            size="sm"
            variant="ghost"
            {...rest}
          >
            <Icon
              className="text-primary"
              name="phosphor-info"
              size="lg"
              weight="fill"
            />
            <span className="sr-only">{buttonLabel}</span>
          </Button>
        </DialogTrigger>

        <DialogContent>
          <DialogHeader>
            <DialogTitle className="mb-6 font-bold leading-none">
              {title}
            </DialogTitle>
            <DialogDescription>
              <LearnMoreContent items={items} />
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          isIcon
          aria-label={buttonLabel}
          className={cn(buttonVariant, className)}
          {...rest}
        >
          <Icon name="info" />
          <span className="sr-only">{buttonLabel}</span>
        </Button>
      </PopoverTrigger>

      <PopoverContent side="top">
        <h4 className="mb-6 font-bold leading-none">{title}</h4>
        <LearnMoreContent items={items} />
        <PopoverClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <Icon className="size-4" name="x" />
          <span className="sr-only">{closeLabel}</span>
        </PopoverClose>
      </PopoverContent>
    </Popover>
  );
};
